import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { Link, Paper, Typography } from '@mui/material';

import { getInsuredPersonName } from '../../utils/customerUtils';
import {
  formatDate,
  contractInTrialMonth,
  GET_CUSTOMER,
  GET_CANCELLATION_PAGE_DATA,
} from '../../shared';
import { ENDED } from '../../shared/contractStatus';
import fetchPolicyCoverageInfo from '../../actions/PolicyActions';
import {
  CANCELLATION_TYPE_REGULAR,
  CANCELLATION_TYPE_WITHDRAWAL_BY_CUSTOMER,
  REGULAR_CANCELATION_TYPES,
  WITHDRAWAL_CANCELLATION_TYPES,
} from '../contractCancellation/ContractCancellationConstants';
import CaretRightIcon from '../../icons/CaretRightIcon';

import { getContractDateToBeDisplayed } from './contractOverviewCardUtils';
import PolicyAndAddonName from './PolicyAndAddonName';
import MonthlyPremium from './MonthlyPremium';

import {
  StyledContractOverviewCard,
  StyledContractOverviewCardCancellationLink,
  StyledContractOverviewCardCancellationStatus,
  StyledContractOverviewCardHeader,
  StyledContractOverviewCardText,
  StyledSpacer,
} from './ContractOverviewCard.styled';
import CustomerOverviewCardIcon from './CustomerOverviewCardIcon';

export default function ContractOverviewCard() {
  const intl = useIntl();
  const locale = intl?.locale;

  const { data: customerData, loading: customerLoading } = useQuery(GET_CUSTOMER);
  const customer = customerData?.customer;

  const {
    key: contractKey,
    policyCategory,
    imCoverage,
    startingAt,
    cancelationDate,
    cancelationType,
    status,
    addOnTrialMonth,
    monthlyPremium,
  } = customer?.contract || {};

  const { name: petName } = customer?.insuredPet || {};

  const { data } = useQuery(GET_CANCELLATION_PAGE_DATA, {
    skip: Boolean(cancelationDate),
  });

  const cancellationPageKey = data?.cancellationPageData?.key;
  const contractCanBeWithdrawn =
    !cancelationDate && WITHDRAWAL_CANCELLATION_TYPES.includes(cancellationPageKey);
  const contractCanBeCancelled =
    !cancelationDate && REGULAR_CANCELATION_TYPES.includes(cancellationPageKey);

  const [policyCoverageInfo, setPolicyCoverageInfo] = useState(null);

  useEffect(() => {
    if (!customerLoading) {
      fetchPolicyCoverageInfo(policyCategory, imCoverage, locale).then(setPolicyCoverageInfo);
    }
  }, [customerLoading, locale, policyCategory, imCoverage]);

  const { contractDateToBeDisplayedLabel, contractDateToBeDisplayed } =
    getContractDateToBeDisplayed(intl, startingAt, cancelationDate, status) || {};

  const showTrialMonth =
    contractInTrialMonth(addOnTrialMonth?.status, addOnTrialMonth?.startingAt, startingAt) ||
    (addOnTrialMonth?.status === 'active' && dayjs(addOnTrialMonth?.startingAt).isAfter(dayjs()));

  if (!customer?.contract) return null;

  return (
    <Paper elevation={0} background="professional_blue">
      <StyledContractOverviewCard>
        <StyledContractOverviewCardHeader>
          <CustomerOverviewCardIcon />

          <Typography variant="h3" color="white" data-hj-suppress="true">
            {getInsuredPersonName(customer)}
          </Typography>

          <StyledSpacer />

          <StyledContractOverviewCardText>
            <Typography variant="h3" data-hj-suppress="true">
              {contractKey}
            </Typography>
          </StyledContractOverviewCardText>
        </StyledContractOverviewCardHeader>

        {petName && (
          <div>
            <Typography variant="h3" color="white">
              <FormattedMessage id="policy.card.label.pet.name" />
            </Typography>

            <StyledContractOverviewCardText>
              <Typography variant="h3">{petName}</Typography>
            </StyledContractOverviewCardText>
          </div>
        )}

        <div>
          <Typography variant="h3" color="white">
            <FormattedMessage id="policy.card.label.policy" />
          </Typography>

          <StyledContractOverviewCardText>
            <PolicyAndAddonName customer={customer} addons={policyCoverageInfo?.addons} />
          </StyledContractOverviewCardText>
        </div>

        {!showTrialMonth && contractDateToBeDisplayedLabel && contractDateToBeDisplayed && (
          <div>
            <Typography variant="h3" color="white">
              {contractDateToBeDisplayedLabel}
            </Typography>

            <StyledContractOverviewCardText>
              <Typography variant="p">{contractDateToBeDisplayed}</Typography>
            </StyledContractOverviewCardText>
          </div>
        )}

        {status !== ENDED && (
          <div>
            <Typography variant="h3" color="white">
              <FormattedMessage id="policy.card.label.monthly_premium" />
            </Typography>

            <MonthlyPremium
              customer={customer}
              monthlyPremium={monthlyPremium}
              addons={policyCoverageInfo?.addons}
            />
          </div>
        )}

        {showTrialMonth && (
          <div>
            <Typography variant="h3" color="white">
              <FormattedMessage id="policy.card.label.addon.trial_month" />
            </Typography>

            <StyledContractOverviewCardText>
              <Typography variant="p">{formatDate(addOnTrialMonth?.startingAt)}</Typography>
            </StyledContractOverviewCardText>
          </div>
        )}

        {cancelationType === CANCELLATION_TYPE_WITHDRAWAL_BY_CUSTOMER && (
          <StyledContractOverviewCardCancellationStatus>
            <Typography variant="legalTexts" color="white">
              <FormattedMessage id="customer_overview_card.cancellation.contract_withdrawn" />
            </Typography>
          </StyledContractOverviewCardCancellationStatus>
        )}

        {cancelationType === CANCELLATION_TYPE_REGULAR && status === ENDED && (
          <StyledContractOverviewCardCancellationStatus>
            <Typography variant="legalTexts" color="white">
              <FormattedMessage id="customer_overview_card.cancellation.contract_cancelled" />
            </Typography>
          </StyledContractOverviewCardCancellationStatus>
        )}

        {cancelationType === CANCELLATION_TYPE_REGULAR && status !== ENDED && (
          <StyledContractOverviewCardCancellationStatus>
            <Typography variant="legalTexts" color="white">
              <FormattedMessage id="customer_overview_card.cancellation.contract_will_be_cancelled" />
            </Typography>
          </StyledContractOverviewCardCancellationStatus>
        )}

        {contractCanBeWithdrawn && (
          <div>
            <Link variant="legalTexts" color="white" href="/contract-cancellation">
              <StyledContractOverviewCardCancellationLink>
                <FormattedMessage id="customer_overview_card.cancellation.withdraw_contract.link" />
                <CaretRightIcon stroke="white" />
              </StyledContractOverviewCardCancellationLink>
            </Link>
          </div>
        )}

        {contractCanBeCancelled && (
          <Link variant="legalTexts" color="white" href="/contract-cancellation">
            <StyledContractOverviewCardCancellationLink>
              <FormattedMessage id="customer_overview_card.cancellation.cancel_contract.link" />
              <CaretRightIcon stroke="white" />
            </StyledContractOverviewCardCancellationLink>
          </Link>
        )}
      </StyledContractOverviewCard>
    </Paper>
  );
}
